import { z } from 'zod'

import { SchemaContact, SchemaCustomfield } from '@/__generated/es-api'
import { isPhone } from '@/app/component/form/utils/validation'
import { ContactPermissions, SelectOption } from '@/app/types'

const getRestrictedPlaceholder = (access: boolean) => (access ? '' : '(Restricted)')

export const sortCustomFields = (customFields: SchemaCustomfield[]) =>
  [...customFields].sort((a, b) => {
    if (a.access && !b.access) return -1 // a is accessible, b is not => a should come first
    if (!a.access && b.access) return 1 // else b should come first
    return 0 // otherwise keep the original order
  })

const getCommonFields = (
  contactPerm: ContactPermissions,
  customFields: SchemaCustomfield[],
  languageOptions: SelectOption<string>[],
  defaultCountryCode: string,
  isSaveDisabled: boolean,
) =>
  ({
    firstName: {
      fieldProps: {
        disabled: !contactPerm.firstName || isSaveDisabled,
        label: 'First name',
        name: 'firstName',
        placeholder: getRestrictedPlaceholder(contactPerm.firstName),
      },
      type: 'text',
    },
    lastName: {
      fieldProps: {
        disabled: !contactPerm.lastName || isSaveDisabled,
        label: 'Last name',
        name: 'lastName',
        placeholder: getRestrictedPlaceholder(contactPerm.lastName),
      },
      type: 'text',
    },
    fullPhoneNumber: {
      fieldProps: {
        defaultCountry: defaultCountryCode,
        disabled: !contactPerm.fullPhoneNumber || isSaveDisabled,
        label: 'Phone number',
        name: 'fullPhoneNumber',
        placeholder: getRestrictedPlaceholder(contactPerm.fullPhoneNumber),
      },
      type: 'tel',
    },
    language: {
      fieldProps: {
        disabled: !contactPerm.language || isSaveDisabled,
        freeSolo: true,
        label: 'Language',
        name: 'language',
        options: languageOptions.map((l) => ({ ...l, value: l.label })), // To fix the sensitive case
        placeholder: getRestrictedPlaceholder(contactPerm.language),
      },
      type: 'autocomplete',
    },
    ...sortCustomFields(customFields).reduce(
      (acc, { access, id, name }) => ({
        ...acc,
        [`customFields${id}`]: {
          fieldProps: {
            disabled: !access || isSaveDisabled,
            label: name,
            name: `customFields${id}`,
            placeholder: getRestrictedPlaceholder(access),
          },
          type: 'text',
        },
      }),
      {},
    ),
  }) as const

export const getCreateContactFields = (
  contactPerm: ContactPermissions,
  customFields: SchemaCustomfield[],
  languageOptions: SelectOption<string>[],
  defaultCountryCode: string,
  isSaveDisabled: boolean,
) => getCommonFields(contactPerm, customFields, languageOptions, defaultCountryCode, isSaveDisabled)

export const getCreateContactSchema = (customFields: SchemaCustomfield[]) =>
  z.object({
    firstName: z.string().optional().describe('First name'),
    lastName: z.string().optional().describe('Last name'),
    fullPhoneNumber: z.string().optional().describe('Phone number').transform(isPhone(true)),
    language: z.string().optional().describe('Language'),
    ...sortCustomFields(customFields).reduce(
      (acc, { id, name }) => ({
        ...acc,
        [`customFields${id}`]: z.string().optional().describe(name),
      }),
      {},
    ),
  })

export const getUpdateContactFields = (
  contactPerm: ContactPermissions,
  customFields: SchemaCustomfield[],
  languageOptions: SelectOption<string>[],
  defaultCountryCode: string,
) =>
  ({
    ...getCommonFields(contactPerm, customFields, languageOptions, defaultCountryCode, false),
    createdAt: { fieldProps: { label: 'Created at', name: 'createdAt', disabled: true }, type: 'datetime' },
    updatedAt: { fieldProps: { label: 'Updated at', name: 'updatedAt', disabled: true }, type: 'datetime' },
    lastUseAt: { fieldProps: { label: 'Last use at', name: 'lastUseAt', disabled: true }, type: 'datetime' },
    carrier: { fieldProps: { label: 'Carrier', name: 'carrier', disabled: true }, type: 'text' },
  }) as const

export const getUpdateContactSchema = (contactPerm: ContactPermissions, customFields: SchemaCustomfield[]) =>
  z.object({
    firstName: z.string().optional().describe('First name'),
    lastName: z.string().optional().describe('Last name'),
    fullPhoneNumber: z.string().optional().describe('Phone number').transform(isPhone(contactPerm.fullPhoneNumber)),
    language: z.string().optional().describe('Language'),
    ...sortCustomFields(customFields).reduce(
      (acc, { id, name }) => ({
        ...acc,
        [`customFields${id}`]: z.string().optional().describe(name),
      }),
      {},
    ),
    createdAt: z.string().optional().describe('Created at'),
    updatedAt: z.string().optional().describe('Updated at'),
    lastUseAt: z.string().optional().describe('Last use at'),
    carrier: z.string().optional().describe('Carrier'),
  })

export const getUpdateContactDefaultValues = (
  contact: SchemaContact | undefined,
  customFields: SchemaCustomfield[],
  contactPerm: ContactPermissions,
) => ({
  firstName: contact?.firstName && contactPerm.firstName ? contact.firstName : '',
  lastName: contact?.lastName && contactPerm.lastName ? contact.lastName : '',
  fullPhoneNumber: contact?.fullPhoneNumber && contactPerm.fullPhoneNumber ? `+${contact.fullPhoneNumber}` : '',
  language: contact?.language && contactPerm.language ? contact.language : '',
  ...customFields.reduce(
    (acc, { access, id }) => ({
      ...acc,
      [`customFields${id}`]: access && contact?.customFields?.[id] ? contact.customFields[id] : '',
    }),
    {},
  ),
  createdAt: contact?.createdAt || '',
  updatedAt: contact?.updatedAt || '',
  lastUseAt: contact?.lastUseAt || '',
  carrier: contact?.carrier || '',
})
